import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, TrendingUp, Trophy, LogOut, Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Snapshot", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/intake", label: "My intake", icon: FileText, testId: "nav-intake" },
  { to: "/net-worth", label: "Net Worth Tracker", icon: TrendingUp, testId: "nav-networth" },
  { to: "/achievements", label: "Achievements", icon: Trophy, testId: "nav-achievements" },
];

function NavList({ pathname, onClick }) {
  return (
    <nav className="space-y-1.5 flex-1">
      {NAV_ITEMS.map(({ to, label, icon: Icon, testId }) => {
        const active = pathname === to || (to === "/intake" && pathname.startsWith("/intake"));
        return (
          <Link key={to} to={to} data-testid={testId} onClick={onClick}
            className={`flex items-center gap-3 px-3.5 py-2.5 rounded-xl text-sm transition-all ${active ? "bg-[#1A3626] text-white" : "text-[#1C1D1C] hover:bg-[#F4F2EC]"}`}>
            <Icon className="h-4 w-4" strokeWidth={1.6}/>
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

function UserBlock({ user, onLogout }) {
  return (
    <div className="border-t border-[#E5E2DA] pt-4 mt-4">
      <div className="flex items-center gap-2 mb-3 px-2">
        {user?.picture && <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover border border-[#E5E2DA]"/>}
        <div className="text-xs">
          <div className="font-medium text-[#1C1D1C] truncate max-w-[10rem]">{user?.name}</div>
          <div className="text-[#8A8D8A] truncate max-w-[10rem]">{user?.email}</div>
        </div>
      </div>
      <button data-testid="sidebar-logout" onClick={onLogout}
        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm text-[#5B5E5B] hover:bg-[#F4F2EC] transition-colors">
        <LogOut className="h-4 w-4"/> Sign out
      </button>
    </div>
  );
}

export default function ClientSidebar({ user, onLogout }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  return (
    <>
      {/* Mobile header bar with hamburger */}
      <div className="lg:hidden sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-[#E5E2DA] flex items-center justify-between px-5 py-3">
        <Link to="/" className="inline-flex items-center">
          <img src="/wah-logo.png" alt="Wealth Accelerator Hub" className="h-9 w-auto"/>
        </Link>
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(true)}
          className="h-10 w-10 rounded-full bg-[#F4F2EC] flex items-center justify-center text-[#1A3626]">
          <Menu className="h-5 w-5"/>
        </button>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <button aria-label="Close menu" data-testid="mobile-menu-backdrop"
            className="absolute inset-0 bg-[#1C1D1C]/40 backdrop-blur-sm" onClick={() => setOpen(false)}/>
          <aside className="relative w-72 max-w-[85vw] bg-white p-6 flex flex-col h-full shadow-2xl">
            <div className="flex items-center justify-between mb-8">
              <img src="/wah-logo.png" alt="Wealth Accelerator Hub" className="h-10 w-auto"/>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} className="h-9 w-9 rounded-full bg-[#F4F2EC] flex items-center justify-center">
                <X className="h-4 w-4"/>
              </button>
            </div>
            <NavList pathname={pathname} onClick={() => setOpen(false)}/>
            <UserBlock user={user} onLogout={onLogout}/>
          </aside>
        </div>
      )}

      {/* Desktop static sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-[#E5E2DA] bg-white/70 backdrop-blur sticky top-0 h-screen p-6">
        <Link to="/" className="block mb-8">
          <img src="/wah-logo.png" alt="Wealth Accelerator Hub" className="h-10 w-auto"/>
        </Link>
        <NavList pathname={pathname}/>
        <UserBlock user={user} onLogout={onLogout}/>
      </aside>
    </>
  );
}
